/* eslint-disable react-refresh/only-export-components -- co-located helpers/constants */
/**
 * Performance monitoring utilities for the Cnapse Insight
 * Provides metrics tracking, lazy loading helpers, and optimization tools
 */

import React, { lazy } from 'react';
import type { LazyExoticComponent, ComponentType } from 'react';
import { Box } from '@mui/material';

// Performance metrics interface
export interface PerformanceMetrics {
  componentName: string;
  renderTime: number;
  mountTime: number;
  updateCount: number;
  timestamp: number;
}

// Performance monitoring class
class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetrics> = new Map();
  private renderStartTimes: Map<string, number> = new Map();

  startRender(componentName: string): void {
    this.renderStartTimes.set(componentName, performance.now());
  }

  endRender(componentName: string): void {
    const startTime = this.renderStartTimes.get(componentName);
    if (startTime) {
      const renderTime = performance.now() - startTime;
      this.updateMetrics(componentName, { renderTime });
      this.renderStartTimes.delete(componentName);
    }
  }

  recordMount(componentName: string, mountTime: number): void {
    this.updateMetrics(componentName, { mountTime });
  }

  recordUpdate(componentName: string): void {
    const current = this.metrics.get(componentName);
    const updateCount = current ? current.updateCount + 1 : 1;
    this.updateMetrics(componentName, { updateCount });
  }

  private updateMetrics(componentName: string, updates: Partial<PerformanceMetrics>): void {
    const current = this.metrics.get(componentName) || {
      componentName,
      renderTime: 0,
      mountTime: 0,
      updateCount: 0,
      timestamp: Date.now(),
    };

    this.metrics.set(componentName, {
      ...current,
      ...updates,
      timestamp: Date.now(),
    });
  }

  getMetrics(): PerformanceMetrics[] {
    return Array.from(this.metrics.values());
  }

  getMetricsForComponent(componentName: string): PerformanceMetrics | undefined {
    return this.metrics.get(componentName);
  }

  clearMetrics(): void {
    this.metrics.clear();
    this.renderStartTimes.clear();
  }

  // Report performance issues
  reportSlowComponents(threshold = 100): PerformanceMetrics[] {
    return this.getMetrics().filter(
      metric => metric.renderTime > threshold || metric.mountTime > threshold
    );
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

// HOC for performance monitoring
export function withPerformanceMonitoring<P extends object>(
  WrappedComponent: ComponentType<P>,
  componentName?: string
) {
  const displayName = componentName || WrappedComponent.displayName || WrappedComponent.name || 'Component';

  return React.memo((props: P) => {
    React.useEffect(() => {
      const mountStart = performance.now();
      performanceMonitor.recordMount(displayName, performance.now() - mountStart);
    }, []);

    React.useEffect(() => {
      performanceMonitor.recordUpdate(displayName);
    });

    performanceMonitor.startRender(displayName);
    
    try {
      return <WrappedComponent {...props} />;
    } finally {
      performanceMonitor.endRender(displayName);
    }
  });
}

const LazyLoadStatusMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ p: 2.5, textAlign: 'center', color: 'text.secondary' }}>{children}</Box>
);

// Error boundary component for lazy loading
const LazyLoadErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <React.Suspense
      fallback={<LazyLoadStatusMessage>Loading component...</LazyLoadStatusMessage>}
    >
      {children}
    </React.Suspense>
  );
};

// Lazy loading utility with error boundaries
export function lazyComponentImport(
  importFn: () => Promise<unknown>,
): () => Promise<{ default: ComponentType }> {
  return async () => {
    const module = await importFn() as { default: ComponentType };
    return { default: module.default };
  };
}

export function createLazyComponent(
  factory: () => Promise<{ default: ComponentType }>,
  fallbackComponent?: ComponentType,
): LazyExoticComponent<ComponentType> {
  const FallbackComponent =
    fallbackComponent ||
    (() => (
      <LazyLoadStatusMessage>Failed to load component. Please refresh the page.</LazyLoadStatusMessage>
    ));

  return lazy(async () => {
    try {
      const startTime = performance.now();
      const module = await factory();
      const loadTime = performance.now() - startTime;
      
      // Log lazy loading performance
      console.debug(`Lazy loaded component in ${loadTime.toFixed(2)}ms`);
      
      return module;
    } catch (error) {
      console.error('Failed to lazy load component:', error);
      return { default: FallbackComponent as ComponentType };
    }
  });
}

// Bundle size analyzer
export const bundleAnalyzer = {
  logBundleSize: (moduleName: string, size: number) => {
    console.debug(`Bundle size for ${moduleName}: ${(size / 1024).toFixed(2)}KB`);
  },
  
  trackAsyncImport: async function<T>(importPromise: Promise<T>, moduleName: string): Promise<T> {
    const startTime = performance.now();
    try {
      const result = await importPromise;
      const loadTime = performance.now() - startTime;
      console.debug(`Async import ${moduleName} completed in ${loadTime.toFixed(2)}ms`);
      return result;
    } catch (error) {
      console.error(`Failed to import ${moduleName}:`, error);
      throw error;
    }
  }
};

// Memory usage tracking
interface PerformanceMemory {
  usedJSHeapSize?: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory;
}

export const memoryTracker = {
  getCurrentUsage: (): number | undefined => {
    if ('memory' in performance) {
      return (performance as PerformanceWithMemory).memory?.usedJSHeapSize;
    }
    return undefined;
  },
  
  logMemoryUsage: (context: string): void => {
    const usage = memoryTracker.getCurrentUsage();
    if (usage) {
      console.debug(`Memory usage at ${context}: ${(usage / 1024 / 1024).toFixed(2)}MB`);
    }
  },
  
  trackMemoryLeak: (componentName: string): () => void => {
    const initialUsage = memoryTracker.getCurrentUsage();
    
    return () => {
      const finalUsage = memoryTracker.getCurrentUsage();
      if (initialUsage && finalUsage && finalUsage > initialUsage * 1.1) {
        console.warn(
          `Potential memory leak in ${componentName}. ` +
          `Usage increased from ${(initialUsage / 1024 / 1024).toFixed(2)}MB ` +
          `to ${(finalUsage / 1024 / 1024).toFixed(2)}MB`
        );
      }
    };
  }
};

// Performance optimization hooks
export const usePerformanceOptimization = (): {
  renderCount: number;
  reportPerformance: () => void;
  clearMetrics: () => void;
} => {
  const [renderCount, setRenderCount] = React.useState(0);

  React.useEffect(() => {
    setRenderCount(prev => prev + 1);
  }, []);

  const reportPerformance = React.useCallback(() => {
    const metrics = performanceMonitor.getMetrics();
    const slowComponents = performanceMonitor.reportSlowComponents();

    console.group('Performance Report');
    console.table(metrics);
    if (slowComponents.length > 0) {
      console.warn('Slow components detected:', slowComponents);
    }
    console.groupEnd();
  }, []);

  return {
    renderCount,
    reportPerformance,
    clearMetrics: performanceMonitor.clearMetrics,
  };
};

// Debounce utility for performance optimization
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Throttle utility for performance optimization
export function useThrottle<T extends (...args: never[]) => unknown>(
  callback: T,
  delay: number
): T {
  const throttledCallback = React.useCallback(
    (...args: Parameters<T>) => {
      let shouldWait = false;
      
      if (!shouldWait) {
        callback(...args);
        shouldWait = true;
        setTimeout(() => {
          shouldWait = false;
        }, delay);
      }
    },
    [callback, delay]
  ) as T;

  return throttledCallback;
}

// Virtual scrolling utility for large lists
export const useVirtualScrolling = <T,>(
  items: T[],
  itemHeight: number,
  containerHeight: number
): {
  visibleItems: T[];
  totalHeight: number;
  offsetY: number;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
} => {
  const [scrollTop, setScrollTop] = React.useState(0);

  const visibleStartIndex = Math.floor(scrollTop / itemHeight);
  const visibleEndIndex = Math.min(
    visibleStartIndex + Math.ceil(containerHeight / itemHeight) + 1,
    items.length - 1
  );

  const visibleItems = items.slice(visibleStartIndex, visibleEndIndex + 1);
  const totalHeight = items.length * itemHeight;
  const offsetY = visibleStartIndex * itemHeight;

  return {
    visibleItems,
    totalHeight,
    offsetY,
    onScroll: (e: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(e.currentTarget.scrollTop);
    },
  };
};

export { LazyLoadErrorBoundary };

export default {
  performanceMonitor,
  withPerformanceMonitoring,
  createLazyComponent,
  bundleAnalyzer,
  memoryTracker,
  usePerformanceOptimization,
  useDebounce,
  useThrottle,
  useVirtualScrolling,
  LazyLoadErrorBoundary,
};